import { useEffect, useRef } from "react";
import { useHydrated } from "remix-utils/use-hydrated";

import { PrefetchBuilderAnchors } from "~/hec/components/prefetch-builder-anchors";
import { rewriteSfccContentLinks } from "~/hec/utils/sfcc-content-links";

import { stripStraySemicolonsAfterScripts } from "../utils/strip-stray-script-semicolons";

type ModuleKey = "top" | "1" | "2" | "3";

interface FeatureModuleEntry {
  key: ModuleKey;
  html: string | undefined;
  displayFrom: unknown;
  displayTo: unknown;
}

const ALL_FEATURE_MODULE_KEYS: ModuleKey[] = ["top", "1", "2", "3"];

/**
 * Schedule window check: returns true when `now` falls inside the
 * (optional) [displayFrom, displayTo] window. Either bound can be
 * missing; both missing = always-on.
 */
function isWithinDisplayWindow(
  displayFrom: unknown,
  displayTo: unknown,
  now: number,
): boolean {
  const fromMs = displayFrom ? new Date(displayFrom as string).getTime() : null;
  const toMs = displayTo ? new Date(displayTo as string).getTime() : null;
  if (fromMs === null && toMs === null) return true;
  if (fromMs !== null && toMs !== null) return now >= fromMs && now <= toMs;
  if (fromMs === null && toMs !== null) return now <= toMs;
  if (fromMs !== null && toMs === null) return now >= fromMs;
  return false;
}

function buildCandidateModules(
  product: Record<string, unknown>,
): FeatureModuleEntry[] {
  return [
    {
      key: "top",
      html: product.c_moduleTopFeatureContent as string | undefined,
      displayFrom: product.c_moduleTopFeatureContentDisplayFrom,
      displayTo: product.c_moduleTopFeatureContentDisplayTo,
    },
    {
      key: "1",
      html: product.c_module1FeatureContent as string | undefined,
      displayFrom: product.c_featureContentModule1DisplayFrom,
      displayTo: product.c_featureContentModule1DisplayTo,
    },
    {
      key: "2",
      html: product.c_module2FeatureContent as string | undefined,
      displayFrom: product.c_featureContentModule2DisplayFrom,
      displayTo: product.c_featureContentModule2DisplayTo,
    },
    {
      key: "3",
      html: product.c_module3FeatureContent as string | undefined,
      displayFrom: product.c_featureContentModule3DisplayFrom,
      displayTo: product.c_featureContentModule3DisplayTo,
    },
  ];
}

/**
 * Renders one BM feature-content blob and revives any `<script>` embeds it
 * contains on client navigation.
 *
 * `dangerouslySetInnerHTML` never executes `<script>` (HTML spec: scripts set
 * via innerHTML don't run), so on a soft nav (PLP -> PDP) the third-party embeds
 * (e.g. cas.zma.gs banners) never fire — until a refresh forces a fresh document
 * parse. We re-create each `<script>` so the browser executes it.
 *
 * `useHydrated()` is the guard: it's false on the hard-load hydration render
 * (the SSR'd scripts were already executed by the HTML parser, so re-running
 * them would double-inject) and true on a soft-nav first mount (the parser
 * never saw this markup). Captured at mount so the later false->true flip on
 * the hard-loaded page can't re-fire the effect. `ran` keeps it idempotent
 * within a mount. (SFCC-11655.)
 *
 * ponytail: per-mount re-inject — the banner reappears on back/forward because
 * each fresh PDP mount re-clones. A non-idempotent embed could re-register
 * globals across many visits in one session; QA against the real cas.zma.gs init.
 */
function FeatureModuleHtml({
  html,
  className,
}: {
  html: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const hydratedAtMount = useRef(useHydrated());
  const ran = useRef(false);

  useEffect(() => {
    if (!hydratedAtMount.current || ran.current || !ref.current) return;
    ran.current = true;
    for (const old of ref.current.querySelectorAll("script")) {
      const script = document.createElement("script");
      for (const attr of old.attributes) {
        script.setAttribute(attr.name, attr.value);
      }
      // Inline body — ignored by the browser when `src` is set.
      // ponytail: no nonce copy. CSP is disabled today; when it's re-enabled,
      // inline blobs would need the page nonce set here (external-src embeds are
      // host-allowlisted, not nonce-gated). Add it with that ticket + a test.
      script.textContent = old.textContent;
      old.replaceWith(script);
    }
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
      suppressHydrationWarning
    />
  );
}

/**
 * Feature content modules — scheduled HTML from product custom attributes.
 *
 * TODO: Migrate this content to Builder.io. These are raw HTML blobs stored
 * on product custom attributes in BM. Need to audit all content, then
 * determine automated migration path to Builder blocks keyed by product ID.
 *
 * `includedKeys` controls which slots render, so the same component drives three
 * placements: the `top` slot renders on its own at the page top (above the
 * breadcrumbs, mirroring SFRA's `topContentModule` include at the head of
 * `productDetails.isml`), `1/3` render grouped above the accordions
 * (SFCC-11805), and `2` is the below-accordions fallback when the Builder
 * inline entry has no `belowAccordions` content (COM-793 — SFRA rendered it
 * from the accordion template, `customAttributes.isml`). WDH omits `top` entirely to mirror the SFRA `app_wdh` cartridge
 * override that strips the `topContentModule` include.
 *
 * Audit row: PDP-WDH-01 (omit "top" for WDH).
 */
export function FeatureContentModules({
  product,
  includedKeys = ALL_FEATURE_MODULE_KEYS,
  className = "mt-8 w-full space-y-6",
  testId = "feature-content-modules",
}: {
  product: Record<string, unknown>;
  includedKeys?: ModuleKey[];
  /**
   * Wrapper `<section>` class. Defaults to the grouped placement above the
   * accordions (`mt-8`); the `top` slot renders separately at the page top
   * with its own spacing (SFCC-11805).
   */
  className?: string;
  /**
   * `data-testid` for the wrapper. Distinct per placement so the page-top
   * `top` slot and the grouped `1/2/3` section don't collide (SFCC-11805).
   */
  testId?: string;
}) {
  const now = Date.now();
  const allowedKeys = new Set(includedKeys);
  const modules = buildCandidateModules(product).filter(
    entry =>
      allowedKeys.has(entry.key) &&
      entry.html &&
      isWithinDisplayWindow(entry.displayFrom, entry.displayTo, now),
  );

  if (modules.length === 0) return null;

  return (
    <section className={className} data-testid={testId}>
      <PrefetchBuilderAnchors>
        {modules.map(entry => (
          <FeatureModuleHtml
            key={`feature-module-${entry.key}`}
            className="prose prose-sm max-w-none text-foreground"
            // COM-792: BM embeds pasted as `<script …></script>;` would
            // otherwise render the `;` as visible text.
            html={rewriteSfccContentLinks(
              stripStraySemicolonsAfterScripts(entry.html!),
            )}
          />
        ))}
      </PrefetchBuilderAnchors>
    </section>
  );
}
